/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import map from 'lodash/map';
import set from 'lodash/set';
import { Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { gnoment } from 'shared-ui/gnoment';
import isEmail from 'validator/lib/isEmail';

import {
    Button,
    ButtonGroup,
    ContentBlock,
    FlexCol,
    FlexRow,
    Icon,
    ModalActions,
    NotFoundPage,
    NotificationActions,
    NotificationContainer,
    Stack,
    StickyBottomHelper,
    StyledModal,
} from 'sarsaparilla';
import { CompatRouterProps, withCompatRouter } from 'sarsaparilla/legacy';
import { authModalActions } from 'shared-ui/auth/redux';
import SharedCaptchaModal from 'shared-ui/components/RecaptchaModal/SharedCaptchaModal';
import type { RecaptchaWithFallbackResult } from 'shared-ui/recaptcha';
import { RecaptchaCheckbox, withRecaptchaFallback } from 'shared-ui/recaptcha';
import { SendGAEvent } from 'shared-ui/utils/gaCommon';

import { INVENTORY_TREE_PERMIT } from 'ui-field-sales/src/constants/types';
import { orderPageRoute } from '../../dev/routesArray';
import type {
    AvailabilityEntry,
    FormError,
    PermitType,
    Reservation,
    SeasonData,
    TreePermitData,
} from '../../types/permit';
import { fetchAvailability } from '../actions/availability';
import { removeReservation } from '../actions/cart';
import { fetchPermit } from '../actions/permit';
import { fetchPermitTypes } from '../actions/permitTypes';
import { createReservation, fetchReservation } from '../actions/reservation';
import { fetchSeasons } from '../actions/seasons';
import { getParentAsset, getPrice, getValidate } from '../api';
import Banner from '../components/common/Banner';
import CTABar from '../components/TreePermitOrderDetailsPage/CTABar';
import Header from '../components/TreePermitOrderDetailsPage/Header';
import HeaderPlaceholder from '../components/TreePermitOrderDetailsPage/HeaderPlaceholder';
import Holders from '../components/TreePermitOrderDetailsPage/Holders';
import NeedToKnow from '../components/TreePermitOrderDetailsPage/NeedToKnow';
import Permits from '../components/TreePermitOrderDetailsPage/Permits';
import Progress from '../components/TreePermitOrderDetailsPage/Progress';
import Summary from '../components/TreePermitOrderDetailsPage/Summary';
import SummaryPlaceholder from '../components/TreePermitOrderDetailsPage/SummaryPlaceholder';
import { RESERVATION_FEE } from '../constants';
import {
    availabilitySelectors as availabilitySel,
    permitSelectors as locationsSel,
    reservationSelectors as reservationSel,
    seasonsSelector as seasonSel,
    permitTypesSelectors as typesSel,
} from '../reducers';
import { initialReservation, onError } from '../utils';

const mapStateToProps = (state: any) => ({
    treePermit: locationsSel.getPermit(state) as TreePermitData,
    seasons: seasonSel.getSeasons(state) as Record<string, SeasonData>,
    permitTypes: typesSel.getPermitTypes(state) as Array<PermitType>,
    availability: availabilitySel.getAvailability(state) as Record<
        string,
        Partial<AvailabilityEntry>
    >,
    reservation: reservationSel.getReservation(state) as Reservation | undefined,
    treePermitObj: get(state, 'treePermit.permit', false),
    seasonsObj: get(state, 'treePermit.seasons', false),
    rating: get(state, 'ratingReviews.fetchAggregate.data', {}),
    account: state.auth && state.auth.account && state.auth.account.account,
    csrAccount: get(state, 'auth.csrAccount', {}),
    auth: get(state, 'auth', {}),
});

const mapDispatchToProps = {
    getTreePermit: fetchPermit,
    getPermitTypes: fetchPermitTypes,
    getSeasons: fetchSeasons,
    getAvailability: fetchAvailability,
    createReservation,
    getReservation: fetchReservation,
    openLoginModal: authModalActions.openLoginModal,
    deleteReservation: removeReservation,
    setNotification: NotificationActions.setNotification,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface TreePermitOrderDetailsPageTypes extends PropsFromRedux {
    router?: CompatRouterProps;
    recaptcha: RecaptchaWithFallbackResult;
}
const steps = ['Order Details', 'Shopping Cart', 'Payment', 'Confirmation'];
const reservationDefaults = {
    holder: {
        first_name: '',
        last_name: '',
        email: '',
        repeat_email: '',
        zip_code: '',
        confirm: false,
        voucher: '',
    },
    permit: {},
    quantity: 0,
};

export class TreePermitOrderDetailsPage extends Component<TreePermitOrderDetailsPageTypes> {
    state = {
        errors: {} as Record<string, Array<FormError>>,
        isConsentChecked: false,
        isLoading: true,
        isSubmitting: false,
        isSummaryModalOpen: false,
        isChallengeModalOpen: false,
        isNotFound: false,
        hasSaveReservationIntent: false,
        parentLocation: [],
        reservations: [] as Array<Reservation>,
    };

    componentDidMount() {
        const treePermitId = this.props.router?.params?.treePermitId;
        const reservationId = this.props.router?.params?.reservationId;
        const year = gnoment().format('YYYY');
        const {
            getTreePermit,
            getSeasons,
            getPermitTypes,
            getAvailability,
            getReservation,
        } = this.props;
        const permitTypesOnSaleOnly = true;
        if (!isNil(reservationId)) {
            getReservation(reservationId)
                .then(async (item: any) => {
                    try {
                        const {
                            data: { price, fees, total },
                        } = await getPrice({
                            permit_type_id: item?.permit_type_id,
                            trees_number: item?.quantity,
                        });
                        const reservationCurrent = [
                            {
                                holder: {
                                    ...item?.permit_info?.holder_info,
                                    confirm: !isEmpty(item?.permit_info?.ekopass),
                                    repeat_email: item?.permit_info?.holder_info?.email,
                                    voucher: item?.permit_info?.ekopass,
                                },
                                permit: {
                                    description: item?.permit_info?.permit_name,
                                    facility_id: item?.facility_id,
                                    facility_type: '',
                                    fees,
                                    price: item.price || price,
                                    total,
                                    sku: item?.sku,
                                    max_trees: item?.quantity,
                                    name: item?.permit_info?.permit_name,
                                    id: item?.permit_type_id,
                                },
                                quantity:
                                    item?.permit_info?.trees_number || item?.quantity,
                            },
                        ];
                        this.setState({
                            reservations: reservationCurrent,
                        });
                    } catch (e: any) {
                        this.notifyError(e);
                    }
                })
                .catch((e: any) => this.notifyError(e));
        }
        getTreePermit(treePermitId ?? '')
            ?.then((result: Record<string, unknown>) => {
                const { id, parent_asset_id } = result as {
                    id: string;
                    parent_asset_id: string;
                };
                Promise.all([
                    getSeasons(id, year),
                    getPermitTypes(id, permitTypesOnSaleOnly),
                    getAvailability(id, year),
                ])
                    ?.then(() => {
                        this.setState({ isLoading: false });
                        getParentAsset(parent_asset_id)
                            .then(({ data: { results = [] } }) =>
                                // eslint-disable-next-line react/no-unused-state
                                this.setState({ parentLocation: results })
                            )
                            .catch((e: any) => this.notifyError(e));
                    })
                    ?.catch((e) => this.notifyError(e));
            })
            ?.catch((e: any) => this.notifyError(e));
    }

    componentDidUpdate(prevProps: TreePermitOrderDetailsPageTypes) {
        const { account } = this.props;
        const { hasSaveReservationIntent } = this.state;
        if (!prevProps.account && account && hasSaveReservationIntent) {
            // setState is OK, checking for difference in account and hasSaveReservationIntent
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ hasSaveReservationIntent: false }, this.onSubmitHandler);
        }
    }

    notifyError = (e: { message?: string | undefined }) => {
        const { setNotification } = this.props;
        const err = onError(e, 'There was a problem loading the tree permits.');
        setNotification('error', err);
    };

    onChangeConsentHandler = (isConsentChecked = false) => {
        this.setState({
            isConsentChecked,
        });
    };

    onChangeReservationHandler = async (key = '', value = '') => {
        if (!key) {
            return;
        }

        const { permitTypes: permits } = this.props;
        const permit = permits.find((item) => {
            return item?.id === key;
        });

        const valueParsed = parseInt(value, 10);

        let reservations = cloneDeep(this.state.reservations);
        const reservationIndex = reservations.findIndex((item) => {
            return item.permit.id === key;
        });

        try {
            // get price for selected trees
            const {
                data: { price, fees, total },
            } = await getPrice({ permit_type_id: permit?.id, trees_number: valueParsed });

            // The number of trees were updated from n to 0
            if (!valueParsed) {
                reservations = reservations.filter((item) => {
                    return item?.permit?.id !== key;
                });
            }
            // The permit is already on reservations and we just need to update the number of trees
            else if (reservationIndex !== -1) {
                reservations[reservationIndex].quantity = valueParsed;
                reservations[reservationIndex].permit.price = price;
                reservations[reservationIndex].permit.fees = fees;
                reservations[reservationIndex].permit.total = total;
            }
            // This is the first time that the user adds a permit
            else {
                const reservation = cloneDeep(reservationDefaults);
                if (this.props.account != null) {
                    reservation.holder.first_name = this.props.account.first_name;
                    reservation.holder.last_name = this.props.account.last_name;
                    reservation.holder.email = this.props.account.email;
                    reservation.holder.repeat_email = this.props.account.email;
                    reservation.holder.zip_code =
                        this.props.account.home_address.zip_code;
                }
                if (permit) {
                    reservations.push({
                        ...reservation,
                        permit: {
                            ...permit,
                            price,
                            fees,
                            total,
                        },
                        quantity: valueParsed,
                    });
                }
            }
            this.setState({
                reservations,
            });
        } catch (e: any) {
            this.notifyError(e);
        }
    };

    // eslint-disable-next-line react/sort-comp
    calculatePriceWithDiscount = debounce(async (reservations) => {
        const updated = map(reservations, async (item) => {
            const voucher = item?.holder?.voucher;
            const confirm = item?.holder?.confirm;
            if (!confirm) {
                const { data: val } = await getPrice({
                    permit_type_id: item?.permit?.id,
                    trees_number: item?.quantity,
                });
                return {
                    ...item,
                    holder: {
                        ...item?.holder,
                        voucher: '',
                    },
                    permit: {
                        ...item?.permit,
                        price: val?.price,
                        total: val?.total,
                        fees: val?.fees,
                    },
                };
            }
            const { data } = await getPrice({
                permit_type_id: item?.permit?.id,
                trees_number: item?.quantity,
                ekopass: voucher,
            });
            return {
                ...item,
                permit: {
                    ...item?.permit,
                    price: data?.price,
                    total: data?.total,
                    fees: data?.fees,
                },
            };
        });
        await Promise.all(updated)
            .then((newReservations) => {
                this.setState({
                    reservations: newReservations,
                });
            })
            .catch((e) => this.notifyError(e));
    }, 2000);

    onChangeHandler = (key = '', value?: string | boolean) => {
        if (!key) {
            return;
        }

        const reservations = set(cloneDeep(this.state.reservations), key, value);

        if (key.indexOf('voucher') !== -1 || key.indexOf('confirm') !== -1) {
            this.calculatePriceWithDiscount(reservations);
        }

        this.setState({
            reservations,
        });
    };

    removeReservationHandler = async (key = '') => {
        if (!key) {
            return;
        }

        const reservation = cloneDeep(this.state.reservations).find((item) => {
            return item?.permit?.id === key;
        });
        const permitId = reservation?.permit?.id;
        const permitName = reservation?.permit?.name ?? '';
        const reservations = cloneDeep(this.state.reservations).filter((item) => {
            return item?.permit?.id !== permitId;
        });
        const errors = cloneDeep(this.state.errors);

        if (errors[permitName]) {
            delete errors[permitName];
        }

        this.setState({
            errors,
            reservations,
        });
        if (this.props?.reservation?.id) {
            try {
                await this.props.deleteReservation(this.props?.reservation?.id);
                window.location.replace('/cart');
            } catch (e: any) {
                this.notifyError(e);
            }
        }
    };

    onSubmitHandler = async () => {
        this.setState({
            isSubmitting: true,
        });
        const {
            getAvailability,
            openLoginModal,
            account,
            reservation,
            treePermit,
            csrAccount,
        } = this.props;
        const { reservations } = this.state;
        const treePermitId = treePermit?.id;
        const year = gnoment().format('YYYY');
        try {
            const availability = (await getAvailability(treePermitId, year)) as Record<
                string,
                Partial<AvailabilityEntry>
            >;
            const errors = this.validationHandler(availability);
            this.setState({
                isSummaryModalOpen: false,
                errors,
            });
            if (!isEmpty(errors)) {
                this.setState({
                    isSubmitting: false,
                });
                return;
            }
        } catch (e: any) {
            this.setState({
                isSubmitting: false,
            });
            this.notifyError(e);
            return;
        }
        const loggedIn = account != null;
        if (!loggedIn) {
            openLoginModal();
            this.setState({ hasSaveReservationIntent: true, isSubmitting: false });
            return;
        }
        const pointOfSale = isNil(csrAccount) ? 'web' : 'call-center';
        const payload = map(reservations, (item) => {
            const values = {
                ...initialReservation,
                id: reservation?.id,
                account_id: account?.account_id,
                facility_id: item?.permit?.facility_id,
                permit_type_id: item?.permit?.id,
                price: item?.permit?.price,
                sku: item?.permit?.sku,
                quantity: item?.quantity,
                point_of_sale: pointOfSale,
                holder_info: {
                    first_name: item?.holder?.first_name,
                    last_name: item?.holder?.last_name,
                    email: item?.holder?.email,
                    zip_code: item?.holder?.zip_code,
                },
                trees_number: item?.quantity,
                ekopass: item?.holder?.voucher,
                fees: item?.permit?.fees,
                total: item?.permit?.total,
            };
            return values;
        });
        let newErrors: any[] = [];
        const validate = map(payload, async (item, i) => {
            const {
                data: { errors: validateErrors },
            } = await getValidate(item);
            if (!isNil(validateErrors)) {
                const getErrors = map(validateErrors, (v) => {
                    const ekoError = v.toLowerCase()?.indexOf('eko') !== -1;
                    const currentError = {
                        errorText: v,
                        inputName: ekoError ? `voucher` : '#',
                        inputId: ekoError ? `voucher-${i}` : '#',
                    };
                    newErrors = [...newErrors, currentError];
                    return currentError;
                });
                this.setState({
                    errors: getErrors,
                    isSubmitting: false,
                });
            }
            return validateErrors;
        });

        await Promise.all(validate)
            .then(async (validationErrors) => {
                const haveErrors = validationErrors.filter(
                    (item) => !isNil(item) && !isEmpty(item)
                );
                if (!isEmpty(haveErrors)) {
                    this.setState({ isSubmitting: false });
                    return;
                }

                const { recaptcha } = this.props;
                const { isChallengeModalOpen } = this.state;

                // A borderline v3 score requires the v2 checkbox. Pop the modal
                // and wait for the user to complete it before re-submitting.
                if (recaptcha.isChallengeRequired && !isChallengeModalOpen) {
                    this.setState({
                        isChallengeModalOpen: true,
                        isSubmitting: false,
                    });
                    return;
                }

                // getToken throws if the reCAPTCHA script failed to load. Do not
                // block the reservation on it - submit without a token and let
                // the backend decide.
                let recaptchaToken: string | null = null;
                try {
                    recaptchaToken = await recaptcha.getToken('create_reservation');
                } catch {
                    recaptchaToken = null;
                }

                const envelope = {
                    reservations: payload,
                    recaptcha_token: recaptchaToken,
                    recaptcha_version: recaptcha.isChallengeRequired ? 'V2' : 'V3',
                };

                try {
                    await this.props.createReservation(envelope);
                    recaptcha.resetChallenge();
                    this.setState({ isChallengeModalOpen: false });
                    setTimeout(() => window.location.assign('/cart'), 1000);
                } catch (e: any) {
                    const { shouldRetry, isFailed } =
                        this.props.recaptcha.handleRecaptchaError(e);

                    if (shouldRetry) {
                        // Score too low - pop the v2 challenge modal for the retry.
                        this.setState({ isChallengeModalOpen: true });
                    } else if (isFailed) {
                        // Hard failure - do not retry, do not reopen the modal.
                        this.setState({ isChallengeModalOpen: false });
                        this.props.setNotification(
                            'error',
                            "We couldn't verify your request. Please try again."
                        );
                    } else {
                        this.setState({
                            errors: [
                                {
                                    errorText: e.message,
                                    inputName: '#',
                                    inputId: '#',
                                },
                            ],
                        });
                        this.notifyError(e);
                    }
                } finally {
                    this.setState({ isSubmitting: false });
                }
            })
            .catch((e) => this.notifyError(e));
    };

    validationHandler = (availability: Record<string, Partial<AvailabilityEntry>>) => {
        const { isConsentChecked, reservations } = this.state;
        const errors: Record<string, Array<FormError>> = {};
        const reservationId = this.props.router?.params?.reservationId;

        reservations.forEach((item, index) => {
            const { holder, permit, quantity } = item;
            const itemErrors = [];

            if (!holder?.first_name) {
                itemErrors.push({
                    errorText: 'First Name is required.',
                    inputName: 'first_name',
                    inputId: `first-name-${index}`,
                });
            }

            if (!holder?.last_name) {
                itemErrors.push({
                    errorText: 'Last Name is required.',
                    inputName: 'last_name',
                    inputId: `last-name-${index}`,
                });
            }

            if (!holder?.email) {
                itemErrors.push({
                    errorText: 'Email is required.',
                    inputName: 'email',
                    inputId: `email-${index}`,
                });
            } else if (!isEmail(holder?.email)) {
                itemErrors.push({
                    errorText: 'Email is not a valid email address.',
                    inputName: 'email',
                    inputId: `email-${index}`,
                });
            }

            if (!holder?.repeat_email) {
                itemErrors.push({
                    errorText: 'Re-enter Email Address is required.',
                    inputName: 'repeat_email',
                    inputId: `confirm-email-${index}`,
                });
            }
            if (!holder?.zip_code) {
                itemErrors.push({
                    errorText: 'Zip code is required.',
                    inputName: 'zip_code',
                    inputId: `zip-code-${index}`,
                });
            } else if (holder?.email !== holder?.repeat_email) {
                itemErrors.push({
                    errorText: 'Email and confirmation do not match.',
                    inputName: 'repeat_email',
                    inputId: `repeat-email-${index}`,
                });
            }

            if (isNil(reservationId) && !isNil(availability)) {
                const availabilityValue =
                    availability[permit.id]?.availability?.available;
                const unlimitedQuota = availability[permit.id]?.availability?.unlimited;
                const isAvailable =
                    (availabilityValue && availabilityValue >= quantity) ||
                    unlimitedQuota;
                if (!isAvailable) {
                    itemErrors.push({
                        errorText: `Not enough quota`,
                        inputName: '#',
                        inputId: '#',
                    });
                }
            }

            const hasItemErrors = !!itemErrors.length;

            if (hasItemErrors) {
                errors[permit?.name] = itemErrors;
            }
        });

        if (!isConsentChecked) {
            errors.General = [
                {
                    errorText:
                        'Please read and agree to the important information below.',
                    inputId: 'consent',
                },
            ];
        }
        return errors;
    };

    toggleSummaryModal = () => {
        const { isSummaryModalOpen } = this.state;
        this.setState({ isSummaryModalOpen: !isSummaryModalOpen });
    };

    closeChallengeModal = () => this.setState({ isChallengeModalOpen: false });

    getReservationTotal = () => {
        const { reservations } = this.state;
        const hasReservations = !!reservations.length;

        if (!hasReservations) {
            return 0;
        }
        const reservationsTotal = reservations.reduce((accumulator, item) => {
            const price = item?.permit?.price;
            return accumulator + price;
        }, 0);
        return reservationsTotal + RESERVATION_FEE;
    };

    render() {
        const {
            errors,
            isConsentChecked,
            isLoading,
            isNotFound,
            isSubmitting,
            parentLocation,
            isSummaryModalOpen,
            reservations,
        } = this.state;
        const { treePermit, availability, seasons, permitTypes, reservation } =
            this.props;

        const total = this.getReservationTotal();

        if (isNotFound) {
            return <NotFoundPage />;
        }

        if (isLoading) {
            return (
                <main id="page-body" className="tp-order-details-page">
                    <Progress activeStep={1} steps={steps} />
                    <ContentBlock className="mt-5 mb-5">
                        <Stack>
                            <FlexRow>
                                <FlexCol lg={8} className="mb-6 mb-lg-0">
                                    <Stack space="xl">
                                        <HeaderPlaceholder />
                                    </Stack>
                                </FlexCol>
                                <FlexCol className="tp-order-details-summary" lg={4}>
                                    <SummaryPlaceholder />
                                </FlexCol>
                            </FlexRow>
                        </Stack>
                    </ContentBlock>
                </main>
            );
        }

        const reservationId = this.props.router?.params?.reservationId;
        // Passing additional data to GA
        const facilityId = treePermit?.asset_id;
        const item_category = INVENTORY_TREE_PERMIT;

        return (
            <main id="page-body" className="tp-order-details-page">
                {/* Fire the add_to_cart GA Event */}
                <SendGAEvent
                    eventType="add_to_cart"
                    itemCategory={item_category}
                    facilityId={facilityId}
                    itemLevel={'Registration Detail'}
                />

                {this.state.isChallengeModalOpen && (
                    <SharedCaptchaModal
                        isOpen={this.state.isChallengeModalOpen}
                        onRequestClose={this.closeChallengeModal}
                        onContinue={this.onSubmitHandler}
                        showCancelButton
                        isContinueDisabled={this.props.recaptcha.isV2Invalid}
                        isContinueLoading={this.state.isSubmitting}
                    >
                        Please complete the challenge below to continue with your
                        reservation.
                        <RecaptchaCheckbox
                            {...this.props.recaptcha.recaptchaCheckboxProps}
                            isFallbackOnly
                            action="create_reservation"
                        />
                    </SharedCaptchaModal>
                )}
                <Banner />
                <NotificationContainer className={'mb-0 mx-6 mt-4'} />
                <Progress activeStep={1} steps={steps} />
                <ContentBlock className="mt-5 mb-5">
                    <Stack>
                        <FlexRow>
                            <FlexCol lg={8} className="mb-6 mb-lg-0">
                                <Stack space="xl">
                                    <Header
                                        errors={errors}
                                        treePermit={treePermit}
                                        parentLocation={parentLocation}
                                    />
                                    <Permits
                                        onChangeReservationHandler={
                                            this.onChangeReservationHandler
                                        }
                                        permits={permitTypes}
                                        reservation={reservation}
                                        availability={availability}
                                        reservations={reservations}
                                    />
                                    <Holders
                                        errors={errors}
                                        onChangeHandler={this.onChangeHandler}
                                        removeReservationHandler={
                                            this.removeReservationHandler
                                        }
                                        reservations={reservations}
                                    />
                                    {!isNil(reservationId) && (
                                        <Button
                                            appearance="secondary"
                                            size="sm"
                                            className="parkpass-add-another-pass"
                                            onClick={() => {
                                                window.location.href = orderPageRoute(
                                                    treePermit.id
                                                );
                                            }}
                                            iconBeforeElement={
                                                <Icon iconName="add-circle" />
                                            }
                                        >
                                            ADD ANOTHER TREE PERMIT
                                        </Button>
                                    )}
                                    <NeedToKnow
                                        contents={
                                            treePermit?.need_to_know?.need_to_know || []
                                        }
                                        errors={errors}
                                        isConsentChecked={isConsentChecked}
                                        onChangeConsentHandler={
                                            this.onChangeConsentHandler
                                        }
                                    />
                                    {!!reservations.length && (
                                        <StickyBottomHelper
                                            className="tp-order-details-cta-bar"
                                            scrollingContainerId="page-body"
                                        >
                                            <div className="rec-order-detail-page-actions">
                                                <CTABar
                                                    onSubmitHandler={this.onSubmitHandler}
                                                    onShowSummaryModal={
                                                        this.toggleSummaryModal
                                                    }
                                                    isSubmitting={isSubmitting}
                                                    reservations={reservations}
                                                    total={total}
                                                />
                                            </div>
                                        </StickyBottomHelper>
                                    )}
                                </Stack>
                            </FlexCol>
                            <FlexCol className="tp-order-details-summary" lg={4}>
                                <Summary
                                    onSubmitHandler={this.onSubmitHandler}
                                    reservations={reservations}
                                    isSubmitting={isSubmitting}
                                    seasons={seasons}
                                    treePermit={treePermit}
                                    permitTypes={permitTypes}
                                    total={total}
                                />
                                <StyledModal
                                    size="md"
                                    onRequestClose={this.toggleSummaryModal}
                                    shouldActionsStickInMobile
                                    isOpen={isSummaryModalOpen}
                                    heading="Summary"
                                >
                                    <>
                                        <Summary
                                            onSubmitHandler={this.onSubmitHandler}
                                            reservations={reservations}
                                            isSubmitting={isSubmitting}
                                            seasons={seasons}
                                            treePermit={treePermit}
                                            permitTypes={permitTypes}
                                            total={total}
                                            isMobile
                                        />
                                        <ModalActions>
                                            <ButtonGroup
                                                isFullWidthOnMobile={false}
                                                isStretchedToFit
                                            >
                                                <Button
                                                    appearance="link"
                                                    onClick={this.toggleSummaryModal}
                                                >
                                                    Go Back
                                                </Button>
                                                <Button
                                                    isDisabled={isSubmitting}
                                                    onClick={this.onSubmitHandler}
                                                >
                                                    Proceed to Cart
                                                </Button>
                                            </ButtonGroup>
                                        </ModalActions>
                                    </>
                                </StyledModal>
                            </FlexCol>
                        </FlexRow>
                    </Stack>
                </ContentBlock>
            </main>
        );
    }
}

export const TreePermitOrderDetailsPageWithRouter = withRecaptchaFallback(
    withCompatRouter(TreePermitOrderDetailsPage)
);

export default connector(TreePermitOrderDetailsPageWithRouter);

// cSpell:ignore ekopass, parkpass
